import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '../../components/layout/AdminLayout';
import { STATUS_META, statusMeta, fmtDate } from './orderConstants';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { AlertTriangle, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminOrdersInbox() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('all');
  const [urgentOnly, setUrgentOnly] = useState(false);
  const [q, setQ] = useState('');

  const fetchOrders = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status !== 'all') params.status = status;
      if (urgentOnly) params.urgent = true;
      if (q) params.q = q;
      const { data } = await axios.get(`${API}/orders`, { params });
      setOrders(data);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally {
      setLoading(false);
    }
  }, [status, urgentOnly, q]);

  useEffect(() => {
    const t = setTimeout(fetchOrders, q ? 300 : 0);
    return () => clearTimeout(t);
  }, [fetchOrders, q]);

  return (
    <AdminLayout title="Commandes" subtitle="Inbox des dossiers de prothèses">
      <div className="flex gap-2 mb-4 flex-wrap items-center">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input className="pl-8 w-64" placeholder="N° dossier, patient, cabinet..." value={q} onChange={(e) => setQ(e.target.value)} data-testid="orders-search-input" />
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-56" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {Object.entries(STATUS_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button variant={urgentOnly ? 'default' : 'outline'} size="sm" className="gap-2" onClick={() => setUrgentOnly((v) => !v)} data-testid="orders-urgent-filter">
          <AlertTriangle className="h-4 w-4" />Urgents
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
      ) : orders.length === 0 ? (
        <Card className="border-dashed"><CardContent className="py-10 text-center text-muted-foreground">Aucun dossier.</CardContent></Card>
      ) : (
        <div className="space-y-2" data-testid="admin-orders-list">
          {orders.map((o) => {
            const m = statusMeta(o.status);
            return (
              <Link key={o.id} to={`/admin/orders/${o.id}`} className="block">
                <Card className="border-border hover:border-primary/40 transition-colors" data-testid={`admin-order-row-${o.order_number}`}>
                  <CardContent className="py-3 flex items-center justify-between gap-3 flex-wrap">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold text-sm">{o.order_number}</span>
                        {o.urgent && <AlertTriangle className="h-4 w-4 text-orange-500" />}
                      </div>
                      <p className="text-sm text-muted-foreground truncate">
                        {o.cabinet_name} · {o.praticien_name} · {o.patient_last_name || o.patient_ref} · {o.product_label}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-xs text-muted-foreground">{fmtDate(o.created_at)}</span>
                      <Badge variant="outline" className={m.cls}>{m.label}</Badge>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
}
